import React, { useCallback, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import BootstrapTheme from "@fullcalendar/bootstrap";
import dayGridPlugin from "@fullcalendar/daygrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin from "@fullcalendar/interaction";
import { DatesSetArg, EventClickArg } from "@fullcalendar/core";
import { DateClickArg } from "@fullcalendar/interaction";
import { Spinner } from "reactstrap";
import type { Holiday } from "features/Holidays/types/holiday.types";
import { getHolidays } from "features/Holidays/services/HolidayService";
import {
  HolidayKind,
  HolidayKindLabel,
} from "features/Holidays/constants/holiday.constants";
import { getHolidayYears } from "features/Holidays/utils/holidayYears";

const KIND_COLOR: Record<HolidayKind, string> = {
  [HolidayKind.PUBLIC]: "info",
  [HolidayKind.COMPANY]: "primary",
  [HolidayKind.OPTIONAL]: "warning",
};

const YEARS = getHolidayYears();

function toYmd(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

type Props = {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (holiday: Holiday) => void;
  onCreateAt: (date: string) => void;
  refreshRef: React.MutableRefObject<(() => void) | null>;
};

export function HolidayCalendarView({
  canCreate,
  canUpdate,
  canDelete,
  onEdit,
  onCreateAt,
  refreshRef,
}: Props) {
  const calendarRef = useRef<FullCalendar>(null);
  const currentYearRef = useRef(new Date().getFullYear());
  const [displayYear, setDisplayYear] = useState(() => new Date().getFullYear());
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchForYear = useCallback(async (year: number) => {
    setLoading(true);
    try {
      const res = await getHolidays({ year, limit: 500 });
      const rows: Holiday[] = res?.data?.rows ?? [];
      setEvents(
        rows.map((h) => {
          const color = KIND_COLOR[h.kind] ?? "secondary";
          return {
            id: String(h.id),
            title: h.name,
            start: h.date,
            allDay: true,
            backgroundColor: "transparent",
            borderColor: "transparent",
            extendedProps: { holiday: h, color },
          };
        }),
      );
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    currentYearRef.current = displayYear;
    fetchForYear(displayYear);
  }, [displayYear, fetchForYear]);

  useEffect(() => {
    refreshRef.current = () => fetchForYear(currentYearRef.current);
  }, [fetchForYear, refreshRef]);

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    const mid = new Date((arg.start.getTime() + arg.end.getTime()) / 2);
    setDisplayYear(mid.getFullYear());
  }, []);

  const handleYearJump = (year: number) => {
    calendarRef.current?.getApi().gotoDate(`${year}-06-01`);
  };

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      if (!canUpdate && !canDelete) return;
      onEdit(info.event.extendedProps.holiday as Holiday);
    },
    [canUpdate, canDelete, onEdit],
  );

  const handleDateClick = useCallback(
    (info: DateClickArg) => {
      if (!canCreate) return;
      const ymd = toYmd(info.date);
      if (events.some((e) => e.start === ymd)) return;
      onCreateAt(ymd);
    },
    [canCreate, onCreateAt, events],
  );

  return (
    <div className="card-body">
      <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-3">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {(
            Object.entries(HolidayKindLabel) as [HolidayKind, string][]
          ).map(([key, label]) => (
            <span
              key={key}
              className={`badge bg-${KIND_COLOR[key]}-subtle text-${KIND_COLOR[key]} border border-${KIND_COLOR[key]}-subtle`}
            >
              {label}
            </span>
          ))}
        </div>
        <select
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={displayYear}
          onChange={(e) => handleYearJump(Number(e.target.value))}
        >
          {YEARS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <style>{`
        .fc-day-today { background: rgba(var(--vz-primary-rgb), 0.06) !important; }
        .holiday-event { border-radius: 4px; padding: 1px 6px; font-size: 0.8rem; cursor: pointer; }
      `}</style>

      <div style={{ position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "rgba(var(--vz-body-bg-rgb, 255,255,255), 0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: 4,
            }}
          >
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner size="sm" />
              <span className="small">Loading…</span>
            </div>
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[
            BootstrapTheme,
            dayGridPlugin,
            multiMonthPlugin,
            interactionPlugin,
          ]}
          initialView="dayGridMonth"
          themeSystem="bootstrap"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "multiMonthYear,dayGridMonth",
          }}
          events={events}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          dateClick={canCreate ? handleDateClick : undefined}
          eventContent={(info) => {
            const color: string =
              info.event.extendedProps.color ?? "secondary";
            return (
              <div
                className={`holiday-event bg-${color}-subtle text-${color} w-100 text-truncate`}
              >
                {info.event.title}
              </div>
            );
          }}
          height="auto"
          dayMaxEvents={5}
          nowIndicator
        />
      </div>
    </div>
  );
}
